
import React, { useState } from 'react';
import { format } from 'date-fns';
import { 
  Table, 
  TableHeader, 
  TableRow, 
  TableHead, 
  TableBody, 
  TableCell 
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  ChevronDown, 
  Calendar, 
  Clock, 
  Mail, 
  User,
  MoreVertical,
  MessageSquare,
  ExternalLink,
  Send
} from 'lucide-react';
import { BookingData } from '@/hooks/use-admin-store';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/hooks/use-toast';

interface BookingsListProps {
  bookings: BookingData[];
  onUpdateStatus: (id: string, status: BookingData['status']) => Promise<void>;
}

const BookingsList: React.FC<BookingsListProps> = ({ bookings, onUpdateStatus }) => {
  const [selectedBooking, setSelectedBooking] = useState<BookingData | null>(null);
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);

  const handleStatusChange = async (id: string, status: BookingData['status']) => {
    await onUpdateStatus(id, status);
  };

  const getStatusBadge = (status: BookingData['status']) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">Pending</Badge>;
      case 'confirmed':
        return <Badge variant="outline" className="bg-blue-100 text-blue-800 hover:bg-blue-100">Confirmed</Badge>;
      case 'completed':
        return <Badge variant="outline" className="bg-green-100 text-green-800 hover:bg-green-100">Completed</Badge>;
      case 'cancelled':
        return <Badge variant="outline" className="bg-red-100 text-red-800 hover:bg-red-100">Cancelled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const handleSendMessage = () => {
    if (!selectedBooking || !message.trim()) return;
    
    setIsSending(true);
    
    // Simulate sending email
    setTimeout(() => {
      setIsSending(false);
      setMessage('');
      toast({
        title: "Message Sent",
        description: `Email sent to ${selectedBooking.email}`,
      });
    }, 1000);
  };

  return (
    <>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[180px]">Name</TableHead>
              <TableHead>Date & Time</TableHead>
              <TableHead>Service Type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {bookings.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="h-24 text-center">
                  No bookings found.
                </TableCell>
              </TableRow>
            ) : (
              bookings.map((booking) => (
                <TableRow key={booking.id}>
                  <TableCell className="font-medium">
                    <div className="flex flex-col">
                      <span>{booking.name}</span>
                      <span className="text-xs text-muted-foreground">{booking.email}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <div className="flex items-center">
                        <Calendar className="mr-2 h-4 w-4" />
                        {format(booking.date, 'MMM d, yyyy')}
                      </div>
                      <div className="flex items-center text-muted-foreground text-xs mt-1">
                        <Clock className="mr-2 h-3 w-3" />
                        {booking.timeSlot}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{booking.serviceType}</Badge>
                  </TableCell>
                  <TableCell>{getStatusBadge(booking.status)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => setSelectedBooking(booking)}
                          >
                            <MessageSquare className="h-3 w-3 mr-1" />
                            View
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-[500px]">
                          <DialogHeader>
                            <DialogTitle>Booking Details</DialogTitle>
                          </DialogHeader>
                          {selectedBooking && (
                            <div className="space-y-4 pt-3">
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <h3 className="font-semibold flex items-center">
                                    <User className="h-4 w-4 mr-2" /> Client
                                  </h3>
                                  <p>{selectedBooking.name}</p>
                                  <p className="text-sm text-muted-foreground">{selectedBooking.email}</p>
                                </div>
                                <div>
                                  <h3 className="font-semibold">Business Type</h3>
                                  <p>{selectedBooking.businessType}</p>
                                </div>
                              </div>
                              
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <h3 className="font-semibold flex items-center">
                                    <Calendar className="h-4 w-4 mr-2" /> Date
                                  </h3>
                                  <p>{format(selectedBooking.date, 'MMMM d, yyyy')}</p>
                                </div>
                                <div>
                                  <h3 className="font-semibold flex items-center">
                                    <Clock className="h-4 w-4 mr-2" /> Time
                                  </h3>
                                  <p>{selectedBooking.timeSlot}</p>
                                </div>
                              </div>
                              
                              <div>
                                <h3 className="font-semibold">Message</h3>
                                <p className="p-3 rounded-md bg-muted/50 mt-1">{selectedBooking.message}</p>
                              </div>
                              
                              <div>
                                <h3 className="font-semibold">Status</h3>
                                <div className="flex gap-2 mt-1">
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="outline" className="w-full">
                                        {getStatusBadge(selectedBooking.status)}
                                        <ChevronDown className="ml-2 h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      <DropdownMenuItem onClick={() => handleStatusChange(selectedBooking.id, 'pending')}>
                                        Pending
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => handleStatusChange(selectedBooking.id, 'confirmed')}>
                                        Confirmed
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => handleStatusChange(selectedBooking.id, 'completed')}>
                                        Completed
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => handleStatusChange(selectedBooking.id, 'cancelled')}>
                                        Cancelled
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>
                              </div>
                              
                              <div className="border-t pt-4 mt-4">
                                <h3 className="font-semibold flex items-center">
                                  <Mail className="h-4 w-4 mr-2" /> Send Message
                                </h3>
                                <Textarea
                                  placeholder="Type your message here..."
                                  className="mt-2"
                                  value={message}
                                  onChange={(e) => setMessage(e.target.value)}
                                />
                                <Button 
                                  className="mt-2 w-full"
                                  disabled={!message.trim() || isSending}
                                  onClick={handleSendMessage}
                                >
                                  {isSending ? (
                                    <>Sending...</>
                                  ) : (
                                    <>
                                      <Send className="mr-2 h-4 w-4" />
                                      Send Email
                                    </>
                                  )}
                                </Button>
                              </div>
                            </div>
                          )}
                        </DialogContent>
                      </Dialog>
                      
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleStatusChange(booking.id, 'confirmed')}>
                            Mark as Confirmed
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleStatusChange(booking.id, 'completed')}>
                            Mark as Completed
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleStatusChange(booking.id, 'cancelled')}>
                            Mark as Cancelled
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => window.open(`mailto:${booking.email}`, '_blank')}
                            className="flex items-center"
                          >
                            <ExternalLink className="mr-2 h-4 w-4" />
                            Email Client
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </>
  );
};

export default BookingsList;
